import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SpecialZoomLevel, Viewer, Worker } from "@react-pdf-viewer/core";
import { getFilePlugin } from "@react-pdf-viewer/get-file";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { querySomTagsByTag, type LscKTagGuidelineEntry, type SomQuerySection } from "@services/somTagsQuery";
import { formatSomTagQueryUserFacingError } from "@utils/somQueryErrorDisplay";
import {
  firstSomSummaryParagraph,
  paragraphizeSomText,
  SOM_SECTION_HEADERS,
} from "@utils/somParagraphize";
import { lscKTagEntryHasPdfUrl, resolveCmsPdfInitialPage } from "@utils/somPdfPage";

/** Must match `pdfjs-dist` in package.json (used by @react-pdf-viewer). */
const PDFJS_DIST_VERSION = "3.11.174";
const PDFJS_WORKER_URL = `https://unpkg.com/pdfjs-dist@${PDFJS_DIST_VERSION}/build/pdf.worker.min.js`;

export interface SomSectionDetailDialogProps {
  open: boolean;
  tag: string | null;
  countryCode: string;
  industry: string;
  onClose: () => void;
}

/** Major CMS SOM appendix headings (longest first for regex alternation). */
const LABELED_SECTION_HEADERS = SOM_SECTION_HEADERS;

const SECTION_HEADING_DISPLAY: Record<string, string> = {
  INTENT: "Intent",
  DEFINITIONS: "Definitions",
  GUIDANCE: "Guidance",
  "INVESTIGATIVE PROCEDURE": "Investigative procedure",
  "INVESTIGATIVE PROCEDURES": "Investigative procedures",
  "KEY ELEMENTS OF NON-COMPLIANCE": "Key elements of non-compliance",
  "DEFICIENCY CATEGORIZATION": "Deficiency categorization",
};

function parseLabeledSections(text: string): {
  preamble: string;
  sections: Array<{ title: string; body: string }>;
} {
  const t = text.replace(/\r\n/g, "\n");
  const escaped = LABELED_SECTION_HEADERS.map((h) =>
    h.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  ).join("|");
  const re = new RegExp(`\\n\\s*(${escaped})\\s*\\n`, "gi");
  const hits: { index: number; title: string; contentStart: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(t)) !== null) {
    const raw = m[1].trim().toUpperCase();
    hits.push({
      index: m.index,
      title: SECTION_HEADING_DISPLAY[raw] ?? raw,
      contentStart: m.index + m[0].length,
    });
  }
  if (hits.length === 0) {
    return { preamble: t.trim(), sections: [] };
  }
  const preamble = t.slice(0, hits[0].index).trim();
  const sections: Array<{ title: string; body: string }> = [];
  for (let i = 0; i < hits.length; i++) {
    const end = i + 1 < hits.length ? hits[i + 1].index : t.length;
    const body = t.slice(hits[i].contentStart, end).trim();
    sections.push({ title: hits[i].title, body });
  }
  return { preamble, sections };
}

function ParagraphBlocks({
  text,
  variant = "body2",
}: {
  text: string;
  variant?: "body2" | "body1";
}) {
  const blocks = useMemo(() => paragraphizeSomText(text), [text]);

  if (blocks.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        width: "100%",
        minWidth: 0,
      }}
    >
      {blocks.map((block, i) => (
        <Typography
          key={i}
          variant={variant}
          sx={{
            color: "#262626",
            fontFamily: "Rethink Sans, sans-serif",
            whiteSpace: "pre-wrap",
            lineHeight: 1.65,
            fontSize: variant === "body1" ? "0.9375rem" : "0.8125rem",
            width: "100%",
            maxWidth: "100%",
            overflowWrap: "break-word",
          }}
        >
          {block}
        </Typography>
      ))}
    </Box>
  );
}

const sectionTitleSx = {
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  color: "#065b70",
  fontFamily: "Rethink Sans, sans-serif",
  textTransform: "uppercase" as const,
};

const cardSx = {
  borderRadius: "10px",
  border: "1px solid #e0eef1",
  backgroundColor: "#fafcfd",
  overflow: "hidden" as const,
};

/**
 * PDF dialog body: parent uses a `key` so this remounts per document/tag.
 * `pageNavigationPlugin` / `getFilePlugin` use hooks — they must run at component top level,
 * not inside `useState(() => …)` (that breaks React and causes “Should have a queue”).
 */
function Cms2786RPdfViewer({
  pdfFileUrl,
  initialPageIndex,
  openInNewTabHref,
  formLabel,
  tagLabel,
}: {
  pdfFileUrl: string;
  initialPageIndex: number;
  openInNewTabHref: string | undefined;
  formLabel: string;
  tagLabel: string;
}) {
  const downloadMetaRef = useRef({ formLabel, tagLabel });
  downloadMetaRef.current = { formLabel, tagLabel };

  const pageNavPlugin = pageNavigationPlugin();
  const getFilePluginInstance = getFilePlugin({
    fileNameGenerator: (file) => {
      const { formLabel: fl, tagLabel: tl } = downloadMetaRef.current;
      if (file.name) {
        const leaf = file.name.split("/").pop()?.split("?")[0] ?? file.name;
        if (leaf.toLowerCase().endsWith(".pdf")) return leaf;
      }
      const safeTag = (tl || "document").replace(/[^\w.-]+/g, "_");
      return `${fl.replace(/\s+/g, "_")}_${safeTag}.pdf`;
    },
  });

  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(0);
  const [goToPageStr, setGoToPageStr] = useState(() => String(initialPageIndex + 1));

  const PdfDownload = getFilePluginInstance.Download;

  const jumpToTargetPage = (targetIndex: number) => {
    pageNavPlugin.jumpToPage(targetIndex);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          px: 2,
          py: 1,
          flexShrink: 0,
          bgcolor: "#eef6f8",
          borderBottom: "1px solid #e5f5f8",
        }}
      >
        <PdfDownload>
          {(dl: { onClick: () => void }) => (
            <Button
              type="button"
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
              onClick={dl.onClick}
              sx={{
                textTransform: "none",
                fontFamily: "Rethink Sans, sans-serif",
                borderColor: "#065b70",
                color: "#065b70",
                mr: 1,
                "&:hover": { borderColor: "#054a5c", backgroundColor: "rgba(6, 91, 112, 0.06)" },
              }}
            >
              Download
            </Button>
          )}
        </PdfDownload>
        <Typography
          variant="body2"
          sx={{ fontFamily: "Rethink Sans, sans-serif", color: "#374151", mr: 0.5 }}
        >
          Page
        </Typography>
        <IconButton
          size="small"
          aria-label="Previous page"
          disabled={pdfNumPages === 0 || pdfCurrentPage <= 0}
          onClick={() => pageNavPlugin.jumpToPreviousPage()}
          sx={{ color: "#065b70" }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          variant="body2"
          sx={{ fontFamily: "Rethink Sans, sans-serif", fontWeight: 600, minWidth: 72 }}
        >
          {pdfNumPages > 0 ? `${pdfCurrentPage + 1} / ${pdfNumPages}` : "— / —"}
        </Typography>
        <IconButton
          size="small"
          aria-label="Next page"
          disabled={pdfNumPages === 0 || pdfCurrentPage >= pdfNumPages - 1}
          onClick={() => pageNavPlugin.jumpToNextPage()}
          sx={{ color: "#065b70" }}
        >
          <ChevronRightIcon />
        </IconButton>
        <TextField
          size="small"
          label="Go to"
          value={goToPageStr}
          onChange={(e) => setGoToPageStr(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || pdfNumPages < 1) return;
            const n = parseInt(goToPageStr, 10);
            if (Number.isNaN(n)) return;
            const clamped = Math.min(Math.max(1, n), pdfNumPages);
            jumpToTargetPage(clamped - 1);
            setGoToPageStr(String(clamped));
          }}
          sx={{ width: 88 }}
          inputProps={{ "aria-label": "Page number" }}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        <Worker workerUrl={PDFJS_WORKER_URL}>
          <Viewer
            fileUrl={pdfFileUrl}
            initialPage={initialPageIndex}
            defaultScale={SpecialZoomLevel.PageWidth}
            enableSmoothScroll={false}
            plugins={[pageNavPlugin, getFilePluginInstance]}
            onDocumentLoad={(e) => {
              const n = e.doc.numPages;
              setPdfNumPages(n);
              const last = Math.max(0, n - 1);
              const target = Math.min(initialPageIndex, last);
              setPdfCurrentPage(target);
              setGoToPageStr(String(target + 1));
              /** Core wires `jumpToPage` in an effect after layout; retry so navigation works for deep pages. */
              const scheduleJump = () => jumpToTargetPage(target);
              scheduleJump();
              window.setTimeout(scheduleJump, 50);
              window.setTimeout(scheduleJump, 200);
              window.setTimeout(scheduleJump, 500);
            }}
            onPageChange={(ev) => {
              setPdfCurrentPage(ev.currentPage);
              setGoToPageStr(String(ev.currentPage + 1));
            }}
            renderLoader={(percent) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  flexDirection: "column",
                  gap: 1,
                  bgcolor: "#fafafa",
                }}
              >
                <CircularProgress size={36} sx={{ color: "#0a829d" }} />
                <Typography variant="body2" sx={{ color: "#737373" }}>
                  Loading PDF… {Math.round(percent)}%
                </Typography>
              </Box>
            )}
            renderError={(err) => (
              <Box
                sx={{
                  p: 3,
                  height: "100%",
                  bgcolor: "#fafafa",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ color: "#a4262c", fontSize: "14px", maxWidth: 420 }}>
                  This PDF could not be loaded in the browser
                  {err.message ? ` (${err.message})` : ""}. The host may block embedding, or your
                  network may block the request.
                </Typography>
                {openInNewTabHref ? (
                  <Link
                    href={openInNewTabHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: 600 }}
                  >
                    Open PDF in a new tab
                  </Link>
                ) : null}
              </Box>
            )}
          />
        </Worker>
      </Box>
    </Box>
  );
}

function Cms2786RGuidelinePanel({
  entry,
  onPdfFlowComplete,
}: {
  entry: LscKTagGuidelineEntry;
  /** Called when the PDF viewer is closed (dismisses the whole SOM/LSC flow when used as the only UI). */
  onPdfFlowComplete?: () => void;
}) {
  const g = entry.tag_guideline;
  const url =
    g.cms_official_url?.trim() || g.cms_som_pdf_url?.trim() || "";
  /** Load the file URL without a hash; initial page comes from `pdf_page_hint` (scanned PDFs are not text-searchable). */
  const pdfFileUrl = url ? url.replace(/#.*$/, "") : "";
  const openInNewTabHref =
    url && g.pdf_open_fragment ? `${url.split("#")[0]}${g.pdf_open_fragment}` : url || undefined;
  const formLabel = g.form_id ?? "CMS-2786R";

  /** Tag label for titles / download filename (PDF is image-based; navigation uses CMS page hint only). */
  const tagLabel = (
    g.pdf_search_query ??
    entry.canonical_tag ??
    g.tag ??
    ""
  ).trim();

  const { pageIndex0: initialPageIndex, source: pageSource } = useMemo(
    () => resolveCmsPdfInitialPage(g.pdf_page_hint, url, g.pdf_open_fragment),
    [g.pdf_page_hint, g.pdf_open_fragment, url],
  );

  const [pdfViewerDismissed, setPdfViewerDismissed] = useState(false);

  useEffect(() => {
    setPdfViewerDismissed(false);
  }, [pdfFileUrl, entry.canonical_tag, entry.requested_tag]);

  const finishPdfFlow = () => {
    setPdfViewerDismissed(true);
    onPdfFlowComplete?.();
  };

  if (!pdfFileUrl) {
    return (
      <Typography
        variant="body2"
        sx={{ color: "#737373", fontFamily: "Rethink Sans, sans-serif", py: 0.5 }}
      >
        No CMS PDF link is available for this tag ({entry.canonical_tag ?? g.tag ?? "K-tag"}).
      </Typography>
    );
  }

  const pdfViewerOpen = !pdfViewerDismissed;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <Dialog
        open={pdfViewerOpen}
        onClose={finishPdfFlow}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        aria-labelledby="cms-pdf-inline-viewer-title"
        PaperProps={{
          sx: {
            /** Definite height so flex children (viewer) do not collapse to 0 — without this, `height: 100%` in the PDF viewer paints nothing. */
            height: "calc(100vh - 32px)",
            maxHeight: "calc(100vh - 32px)",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          id="cms-pdf-inline-viewer-title"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 1.5,
            pr: 1,
            borderBottom: "1px solid #e5f5f8",
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#065b70",
                fontFamily: "Rethink Sans, sans-serif",
              }}
            >
              {formLabel}
              {tagLabel ? ` · ${tagLabel}` : ""}
            </Typography>
            <Typography variant="caption" sx={{ color: "#737373", display: "block", mt: 0.25 }}>
              {pageSource === "hint" && g.pdf_page_hint != null && g.pdf_page_hint > 0
                ? `Opens at CMS suggested page ${g.pdf_page_hint}.`
                : pageSource === "fragment"
                ? `Opens at page ${initialPageIndex + 1} (from document link).`
                : "Opens at page 1—use the page bar to go to any page."}{" "}
              PDF is not text-searchable; locate the tag visually.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <IconButton
              aria-label="Close PDF viewer"
              onClick={finishPdfFlow}
              size="small"
              sx={{ color: "#065b70" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "hidden",
            bgcolor: "#525659",
          }}
        >
          {pdfFileUrl ? (
            <Cms2786RPdfViewer
              key={`${pdfFileUrl}-${initialPageIndex}-${entry.canonical_tag ?? tagLabel}`}
              pdfFileUrl={pdfFileUrl}
              initialPageIndex={initialPageIndex}
              openInNewTabHref={openInNewTabHref}
              formLabel={formLabel}
              tagLabel={tagLabel}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #e5f5f8", px: 2, py: 1 }}>
          <Button
            onClick={finishPdfFlow}
            variant="contained"
            sx={{
              backgroundColor: "#065b70",
              fontFamily: "Rethink Sans, sans-serif",
              textTransform: "none",
              "&:hover": { backgroundColor: "#054a5c" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * First narrative paragraph of the requirement (preamble before labeled subsections).
 * Dense single-line API blobs are paragraphized so Summary is not a wall of text (AB#10692).
 * When blank lines / § structure exist, prefer the first structured slice.
 */
function firstParagraphFromPreamble(preamble: string): string {
  const t = preamble.replace(/\r\n/g, "\n").trim();
  if (!t) return "";

  const byBlankLine = t.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  let block = byBlankLine.length > 1 ? (byBlankLine[0] ?? "") : (byBlankLine[0] ?? t);
  block = block.trim();
  if (!block) return "";

  const lines = block.split("\n");
  const firstLineTrim = (lines[0] ?? "").trim();

  if (firstLineTrim.startsWith("§")) {
    const out: string[] = [];
    for (let i = 0; i < lines.length; i++) {
      const lineTrim = (lines[i] ?? "").trim();
      if (i > 0 && lineTrim.startsWith("§")) break;
      out.push(lines[i] ?? "");
    }
    return out.join("\n").trim();
  }

  if (/\n\s*§/.test(block)) {
    return block.split(/\n(?=\s*§)/)[0]?.trim() ?? block;
  }

  // Dense blob (common for historic penalty citations): first readable paragraph only
  return firstSomSummaryParagraph(block);
}

function SectionBlock({ section }: { section: SomQuerySection }) {
  const { preamble, labeledBodySections, firstRequirementSummary } = useMemo(() => {
      if (!section.requirement_text) {
        return {
          preamble: "",
          labeledBodySections: [] as Array<{ title: string; body: string }>,
          firstRequirementSummary: "",
        };
      }
      const parsed = parseLabeledSections(section.requirement_text);
      return {
        preamble: parsed.preamble,
        labeledBodySections: parsed.sections,
        firstRequirementSummary: firstParagraphFromPreamble(parsed.preamble),
      };
    }, [section.requirement_text]);

  const hasInterpretive =
    Array.isArray(section.interpretative_guidelines) &&
    section.interpretative_guidelines.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, width: "100%", minWidth: 0 }}>
      {/* Summary */}
      <Box
        sx={{
          ...cardSx,
          p: 2,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          background: "linear-gradient(180deg, #f0f9fb 0%, #fafcfd 100%)",
        }}
      >
        <Typography sx={{ ...sectionTitleSx, mb: 0.5 }}>Summary</Typography>
        {firstRequirementSummary ? (
          <Box sx={{ mb: 1.5, width: "100%", minWidth: 0 }}>
            <ParagraphBlocks text={firstRequirementSummary} variant="body2" />
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: "12px",
              color: "#737373",
              fontFamily: "Rethink Sans, sans-serif",
              mb: 1.5,
              lineHeight: 1.45,
            }}
          >
            Tag, citation, and facility type. The full regulatory text is shown once, in
            Requirement below.
          </Typography>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
          {section.tag ? (
            <Chip
              size="small"
              label={section.tag}
              sx={{
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 600,
                backgroundColor: "#e0f4f8",
                color: "#065b70",
              }}
            />
          ) : null}
          {section.appendix_letter ? (
            <Chip
              size="small"
              label={`Appendix ${section.appendix_letter}`}
              variant="outlined"
              sx={{
                fontFamily: "Rethink Sans, sans-serif",
                borderColor: "#99d4e1",
                color: "#065b70",
              }}
            />
          ) : null}
          {section.paragraph_identifier ? (
            <Chip
              size="small"
              label={section.paragraph_identifier}
              variant="outlined"
              sx={{
                fontFamily: "Rethink Sans, sans-serif",
                borderColor: "#c5e8f0",
                color: "#424242",
              }}
            />
          ) : null}
          <Chip
            size="small"
            label={section.facility_type_name}
            sx={{
              fontFamily: "Rethink Sans, sans-serif",
              backgroundColor: "#fff",
              border: "1px solid #e5f5f8",
              color: "#424242",
            }}
          />
        </Box>
        {(section.effective_date || section.revision) && (
          <Typography
            sx={{
              fontSize: "12px",
              color: "#737373",
              fontFamily: "Rethink Sans, sans-serif",
              mb: hasInterpretive ? 1.5 : 0,
            }}
          >
            {section.effective_date ? `Effective ${section.effective_date}` : ""}
            {section.effective_date && section.revision ? " · " : ""}
            {section.revision ? `Revision ${section.revision}` : ""}
          </Typography>
        )}
        {hasInterpretive ? (
          <Box sx={{ mt: 2, width: "100%", minWidth: 0 }}>
            <Typography sx={{ ...sectionTitleSx, mb: 1 }}>
              Interpretive highlights
            </Typography>
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 2.25,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                listStylePosition: "outside",
                "& li": {
                  fontSize: "13px",
                  color: "#424242",
                  fontFamily: "Rethink Sans, sans-serif",
                  mb: 0.75,
                  lineHeight: 1.5,
                },
              }}
            >
              {section.interpretative_guidelines.slice(0, 4).map((g, i) => (
                <Typography
                  key={i}
                  component="li"
                  sx={{
                    whiteSpace: "normal",
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    overflowWrap: "break-word",
                    boxSizing: "border-box",
                  }}
                >
                  {g.length > 600 ? `${g.slice(0, 600).trim()}…` : g}
                </Typography>
              ))}
            </Box>
            {section.interpretative_guidelines.length > 4 ? (
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#9e9e9e",
                  fontFamily: "Rethink Sans, sans-serif",
                  mt: 0.5,
                }}
              >
                +{section.interpretative_guidelines.length - 4} more in full requirement
                below
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>

      <Divider sx={{ borderColor: "#e5f5f8" }} />

      {/* Requirement: one accordion per block so every section is visible in the list */}
      {(preamble || labeledBodySections.length > 0) && (
        <Box>
          <Typography sx={{ ...sectionTitleSx, mb: 1.5 }}>Requirement</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {preamble ? (
              <Accordion
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{
                  ...cardSx,
                  "&:before": { display: "none" },
                  backgroundColor: "#fff",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#065b70" }} />}
                  sx={{
                    minHeight: 48,
                    px: 2,
                    backgroundColor: "#f4fafb",
                    "& .MuiAccordionSummary-content": { my: 1 },
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#065b70",
                        fontFamily: "Rethink Sans, sans-serif",
                      }}
                    >
                      {section.paragraph_identifier || "Regulatory text"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#737373",
                        fontFamily: "Rethink Sans, sans-serif",
                      }}
                    >
                      CFR / regulatory text
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                  <ParagraphBlocks text={preamble} variant="body2" />
                </AccordionDetails>
              </Accordion>
            ) : null}

            {labeledBodySections.map((ls, idx) => (
              <Accordion
                key={`${ls.title}-${idx}`}
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{
                  ...cardSx,
                  "&:before": { display: "none" },
                  backgroundColor: "#fff",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#065b70" }} />}
                  sx={{
                    minHeight: 48,
                    px: 2,
                    backgroundColor: "#f4fafb",
                    "& .MuiAccordionSummary-content": { my: 1 },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    {ls.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                  <ParagraphBlocks text={ls.body} variant="body2" />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      )}

      {/* Survey procedures: own accordion so it appears as a peer section */}
      {(preamble || labeledBodySections.length > 0) && (
        <Divider sx={{ borderColor: "#e5f5f8", my: 2 }} />
      )}
      <Box>
        <Typography sx={{ ...sectionTitleSx, mb: 1.5 }}>Survey procedures</Typography>
        {section.survey_procedures?.length ? (
          <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{
              ...cardSx,
              "&:before": { display: "none" },
              backgroundColor: "#fff",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#065b70" }} />}
              sx={{
                minHeight: 48,
                px: 2,
                backgroundColor: "#f4fafb",
                "& .MuiAccordionSummary-content": { my: 1 },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#065b70",
                    fontFamily: "Rethink Sans, sans-serif",
                  }}
                >
                  Surveyor steps
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#737373",
                    fontFamily: "Rethink Sans, sans-serif",
                  }}
                >
                  {section.survey_procedures.length} step
                  {section.survey_procedures.length === 1 ? "" : "s"}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
              <Box sx={{ m: 0, p: 0 }}>
                {section.survey_procedures.map((p, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "flex-start",
                      mb:
                        i < (section.survey_procedures?.length ?? 0) - 1
                          ? 1.5
                          : 0,
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: "8px",
                        backgroundColor: "#e0f4f8",
                        color: "#065b70",
                        fontSize: "13px",
                        fontWeight: 700,
                        fontFamily: "Rethink Sans, sans-serif",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        p: 1.5,
                        borderRadius: "8px",
                        border: "1px solid #e5f5f8",
                        backgroundColor: "#fafcfd",
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#262626",
                          fontFamily: "Rethink Sans, sans-serif",
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.55,
                        }}
                      >
                        {p}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ) : (
          <Typography
            sx={{
              fontSize: "13px",
              color: "#9e9e9e",
              fontFamily: "Rethink Sans, sans-serif",
              fontStyle: "italic",
            }}
          >
            No survey procedures were returned for this tag.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

const SomSectionDetailDialog: React.FC<SomSectionDetailDialogProps> = ({
  open,
  tag,
  countryCode,
  industry,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<SomQuerySection[]>([]);
  const [lscKTagGuidelines, setLscKTagGuidelines] = useState<LscKTagGuidelineEntry[]>([]);

  useEffect(() => {
    if (!open || !tag?.trim()) {
      setSections([]);
      setLscKTagGuidelines([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSections([]);
    setLscKTagGuidelines([]);

    querySomTagsByTag({
      tag: tag.trim(),
      country_code: countryCode,
      industry,
    })
      .then((res) => {
        if (!cancelled) {
          setSections(res.result?.sections ?? []);
          setLscKTagGuidelines(res.result?.lsc_k_tag_guidelines ?? []);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, tag, countryCode, industry]);

  const somErrorUi = useMemo(
    () => (error ? formatSomTagQueryUserFacingError(error) : null),
    [error],
  );

  const showLscKTagPanel =
    !loading && !error && sections.length === 0 && lscKTagGuidelines.length > 0;
  const dialogTagLabel =
    showLscKTagPanel && lscKTagGuidelines[0]?.canonical_tag
      ? lscKTagGuidelines[0].canonical_tag
      : tag ?? "";

  const lscHasPdfUrl =
    showLscKTagPanel && lscKTagGuidelines.some(lscKTagEntryHasPdfUrl);

  /** LSC tag with a CMS PDF: only the PDF viewer — no empty parent dialog behind it. Closing the PDF runs `onClose`. */
  if (open && !loading && !error && lscHasPdfUrl) {
    return (
      <>
        {lscKTagGuidelines.map((entry, idx) => (
          <Cms2786RGuidelinePanel
            key={`${entry.canonical_tag ?? entry.requested_tag ?? idx}`}
            entry={entry}
            onPdfFlowComplete={onClose}
          />
        ))}
      </>
    );
  }

  /** LSC tag but no PDF URL: small message dialog only. */
  if (open && !loading && !error && showLscKTagPanel && !lscHasPdfUrl) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        aria-labelledby="som-section-lsc-no-pdf-title"
      >
        <DialogTitle
          id="som-section-lsc-no-pdf-title"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
            pt: 2,
            pb: 1,
            borderBottom: "1px solid #e5f5f8",
            background: "linear-gradient(180deg, #fff 0%, #f8fdfe 100%)",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#065b70",
                fontFamily: "Rethink Sans, sans-serif",
              }}
            >
              LSC survey tag · {dialogTagLabel}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#737373",
                fontFamily: "Rethink Sans, sans-serif",
                mt: 0.5,
              }}
            >
              {countryCode} · {industry}
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: "#065b70" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
          {lscKTagGuidelines.map((entry, idx) => (
            <Cms2786RGuidelinePanel
              key={`${entry.canonical_tag ?? entry.requested_tag ?? idx}`}
              entry={entry}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: "1px solid #e5f5f8" }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: "#065b70",
              fontFamily: "Rethink Sans, sans-serif",
              textTransform: "none",
              "&:hover": { backgroundColor: "#054a5c" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      aria-labelledby="som-section-dialog-title"
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: { xs: "calc(100vw - 16px)", sm: "calc(100vw - 32px)" },
          m: { xs: 1, sm: 2 },
        },
      }}
    >
      <DialogTitle
        id="som-section-dialog-title"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          pt: 2,
          pb: 1,
          borderBottom: "1px solid #e5f5f8",
          background: "linear-gradient(180deg, #fff 0%, #f8fdfe 100%)",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#065b70",
              fontFamily: "Rethink Sans, sans-serif",
            }}
          >
            {tag ? `SOM survey tag · ${tag}` : "SOM section"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#737373",
              fontFamily: "Rethink Sans, sans-serif",
              mt: 0.5,
            }}
          >
            {countryCode} · {industry}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: "#065b70" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          pt: 2.5,
          pb: 2,
          px: { xs: 2, sm: 3 },
          backgroundColor: "#fff",
          maxHeight: "min(78vh, 900px)",
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress size={40} sx={{ color: "#0a829d" }} />
          </Box>
        ) : somErrorUi ? (
          <Box
            role="alert"
            sx={{
              borderRadius: "10px",
              border: "1px solid #f0c2c6",
              backgroundColor: "#fdf6f7",
              p: 2.5,
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#7d1e26",
                fontFamily: "Rethink Sans, sans-serif",
                mb: 1.5,
                lineHeight: 1.35,
              }}
            >
              {somErrorUi.title}
            </Typography>
            {somErrorUi.paragraphs.map((para, idx) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{
                  color: "#424242",
                  fontFamily: "Rethink Sans, sans-serif",
                  lineHeight: 1.6,
                  mb: idx < somErrorUi.paragraphs.length - 1 ? 1.25 : 0,
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}
              >
                {para}
              </Typography>
            ))}
          </Box>
        ) : sections.length === 0 ? (
          <Typography
            sx={{
              fontSize: "14px",
              color: "#737373",
              fontFamily: "Rethink Sans, sans-serif",
            }}
          >
            No SOM section was returned for this tag.
          </Typography>
        ) : (
          sections.map((s) => <SectionBlock key={s.id} section={s} />)
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, borderTop: "1px solid #e5f5f8" }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: "#065b70",
            fontFamily: "Rethink Sans, sans-serif",
            textTransform: "none",
            "&:hover": { backgroundColor: "#054a5c" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SomSectionDetailDialog;
